import { create } from 'zustand';

import { normalizeError } from '../../api/core/errors';
import { getStudentEmailDomain } from '../../api/environment';
import { ExamSubmissionRepository } from '../../api/repository/exam-submission';
import { UnitRepository } from '../../api/repository/unit';
import { izteachSession } from '../../api/session';
import type { Id, IztExamQuestion } from '../../api/types';
import { useQltIztContentStore } from '../../stores/qlt';
import { userErrorMessage } from '../../stores/userError';
import { emailPattern } from '../submissions/submissions';
import { examLoadBlock, IZ_SIGNED_OUT, loadClassExamRows, type ExamUnitRef } from './classExams';

export { IZ_SIGNED_OUT };
import { toAttempt, type ExamAttempt } from './examResults';

// per exam; the roster pattern narrows it server side, so only a class retaking one exam hundreds of times hits it
const ATTEMPT_LIMIT = 300;

let loadSeq = 0;
let questionSeq = 0;

/** The roster and the Kho bài tree query that names the class's IZ courses. */
export interface ExamLoadInput {
  codes: string[];
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
}

export interface ExamResultState {
  key: string | null;
  loading: boolean;
  error: string | null;
  attempts: ExamAttempt[];
  courseTitles: Record<string, string>;
  /** exam id to its unit's title, when a token could read it. */
  examTitles: Record<string, string>;
  /** exam id to its test unit, for Mở đề lên bảng. */
  examUnits: Record<string, ExamUnitRef>;
  load: (key: string, input: ExamLoadInput, force?: boolean) => Promise<void>;
  /** The exam whose results card is open. */
  examId: string | null;
  questions: IztExamQuestion[];
  questionsLoading: boolean;
  questionsError: string | null;
  open: (examId: string) => Promise<void>;
  close: () => void;
  /** Names, scores, ranks and who has not submitted; in memory only, hidden again when the card closes. */
  showScores: boolean;
  setShowScores: (show: boolean) => void;
}

export const useExamResultStore = create<ExamResultState>((set, get) => ({
  key: null,
  loading: false,
  error: null,
  attempts: [],
  courseTitles: {},
  examTitles: {},
  examUnits: {},

  async load(key, input, force = false) {
    const state = get();
    if (!force && state.key === key && !state.error && (state.loading || state.attempts.length > 0)) return;
    loadSeq += 1;
    const seq = loadSeq;
    const domain = getStudentEmailDomain();
    const pattern = domain ? emailPattern(input.codes, domain) : '';
    const block = examLoadBlock(domain, pattern, Boolean(izteachSession.accessToken));
    if (block) { set({ key, loading: false, error: block.error, attempts: [] }); return; }
    set({ key, loading: true, error: null, attempts: state.key === key ? state.attempts : [] });
    try {
      const tree = await useQltIztContentStore.getState().fetchTree({ class: input.classId, grade: input.gradeId, subject: input.subjectId });
      const found = await loadClassExamRows({
        unitsOf: UnitRepository.titlesForCourse,
        attemptsOf: (examId) => ExamSubmissionRepository.listByExam(examId, pattern, ATTEMPT_LIMIT),
      }, tree, pattern);
      if (seq !== loadSeq) return;
      const attempts: ExamAttempt[] = [];
      found.rows.forEach((row) => {
        const attempt = toAttempt(row, domain);
        if (attempt) attempts.push(attempt);
      });
      set({ loading: false, attempts, examTitles: { ...get().examTitles, ...found.examTitles }, examUnits: { ...get().examUnits, ...found.examUnits }, courseTitles: { ...get().courseTitles, ...found.courseTitles } });
    } catch (error) {
      if (seq !== loadSeq) return;
      const kind = normalizeError(error).kind;
      set({ loading: false, error: kind === 'unauthorized' || kind === 'forbidden' ? IZ_SIGNED_OUT : userErrorMessage(error) });
    }
  },

  examId: null,
  questions: [],
  questionsLoading: false,
  questionsError: null,

  async open(examId) {
    questionSeq += 1;
    const seq = questionSeq;
    // another exam is another session: scores hide again
    set({ examId, questions: [], questionsLoading: true, questionsError: null, showScores: false });
    try {
      const questions = await ExamSubmissionRepository.questionsOf(examId);
      if (seq !== questionSeq) return;
      set({ questions, questionsLoading: false });
    } catch (error) {
      if (seq !== questionSeq) return;
      set({ questionsLoading: false, questionsError: userErrorMessage(error) });
    }
  },

  close() {
    questionSeq += 1;
    set({ examId: null, questions: [], questionsLoading: false, questionsError: null, showScores: false });
  },

  showScores: false,
  setShowScores(show) {
    set({ showScores: show });
  },
}));
